import BreadcrumbsComponent from '../components/general/Breadcrumbs'

class BreadCrumbsPlugin {
  constructor () {
    this.history = []
    this._hash = {}
    this._key = 'name'
    this._name = this._key
    this._domainKey = 'domain'
    this._currentDomain = undefined
    this._historyCache = {}
    this.rootRoute = null
    this._routesHash = {}
    this._routeHashPopulated = false
    this._excludeRoutesHash = {}
    this._anchorsHash = {}
    this._ignoreFlag = 'breadcrumbsIgnore'
  }

  install (Vue, options) {
    if (!this._routeHashPopulated) this.setup(options)
    Vue.component('breadcrumbs', BreadcrumbsComponent)
    Vue.prototype.$breadcrumbs = this
    Vue.util.defineReactive({}, '$breadcrumbs', this)
  }

  pop () {
    const numberOfItemsInStack = this.history.length
    if (numberOfItemsInStack < 2) {
      this.history = []
      this._hash = {}
    } else {
      this._scrapeStack(this.history[numberOfItemsInStack - 2])
    }
  }

  push (item) {
    if (this._isChangingDomains(item)) {
      this._preserveDomainStackHistory()
      this._setDomain(item)
      this._replayDomainHistory()
    } else {
      this._setDomain(item)
    }

    if (!this._isRouteValid(item) || this._isExcludedRoute(item) || this._crumbInStackIsLast(item) || this._shouldIgnore(item)) return
    else if (this._crumbInStack(item)) this._scrapeStack(item)
    else if (this._isAnchor(item)) this._scrapeStackToAnchor(item)
    else this._appendToStack(item)
  }

  setRootRoute (route, router) {
    if (router && router.options.routes) {
      this.rootRoute = router.options.routes.find(r => r[this._key] === route)
      const firstItem = this.history[0]
      if (!firstItem || firstItem[this._key] !== this.rootRoute[this._key]) {
        Object.keys(this._hash).forEach(key => this._hash[key]++)
      }
      this.history.splice(0, 0, this.rootRoute)
      this._hash[this.rootRoute[this._key]] = 0
    } else {
      this.rootRoute = route
    }
  }

  setup (opts) {
    const { router, root, exclude, name, anchors } = opts || {}
    if (router) {
      if (root) this.setRootRoute(root, router)
      this._recordRoutes(router)
      if (exclude) this._setRoutesToExclude(exclude)
      if (anchors) this._setRouteAnchors(anchors)
    }
    if (name) {
      if (Array.isArray(name) && name.every(n => typeof n === 'string')) this._name = name
      else if (typeof name === 'string') this._name = name
      else console.warn(`Property: [name] must be type String or String[]. The fallback is 'name'`)
    }
  }

  getName (route) {
    return this._extractWithName(route)
  }

  resetToRoot () {
    const spliceIndex = this.rootRoute ? 1 : 0
    this.history = this.history.splice(0, spliceIndex)
    this._setHashToHistory()
  }

  _isChangingDomains (item) {
    const currentDomain = this._currentDomain
    const domain = this._extractDomain(item)
    return currentDomain && domain && currentDomain !== domain
  }

  _extractDomain (item) {
    let domain = item.meta && item.meta[this._domainKey]
    if (!domain) {
      item.matched.forEach(i => {
        if (i.meta && i.meta[this._domainKey]) domain = i.meta[this._domainKey]
      })
    }
    return domain
  }

  _setDomain (item) {
    this._currentDomain = this._extractDomain(item)
  }

  _isAnchor (route) {
    const foundRoute = this._anchorsHash[route[this._key]]
    return typeof foundRoute !== 'undefined'
  }

  _isExcludedRoute (route) {
    return typeof this._excludeRoutesHash[route[this._key]] !== 'undefined'
  }

  _setRoutesToExclude (routesToExclude) {
    this._excludeRoutesHash = routesToExclude.reduce((acc, i) => {
      acc[i] = i
      return acc
    }, {})
  }

  _setRouteAnchors (routeAnchors) {
    const routes = this.rootRoute ? [this.rootRoute[this._key], ...routeAnchors] : routeAnchors
    this._anchorsHash = routes.reduce((acc, i) => {
      acc[i] = i
      return acc
    }, {})
  }

  _isRouteValid (route) {
    if (!this._routeHashPopulated) return true

    const foundRoute = this._routesHash[route[this._key]]
    return typeof foundRoute !== 'undefined'
  }

  _recordRoutes (router) {
    if (this._routeHashPopulated) return
    const reduceRoutes = (routes) => {
      return routes.reduce((acc, r) => {
        const k = r[this._key]
        acc[k] = k
        let extraRoutes = {}
        if (r.children && r.children.length > 0) extraRoutes = reduceRoutes(r.children)
        return Object.assign({}, acc, extraRoutes)
      }, {})
    }
    if (router) this._routesHash = reduceRoutes(router.options.routes)
    this._routeHashPopulated = true
  }

  // _recordInitialRoute (route) {
  //   if (this._isRouteValid(route)) this.push(route)
  //   this._freshLoad = false
  // }

  _crumbInStack (item) {
    return typeof this._hash[item[this._key]] !== 'undefined'
  }

  _crumbInStackIsLast (item) {
    const i = this._hash[item[this._key]]
    return typeof i !== 'undefined' && i === this.history.length - 1
  }

  _scrapeStack (item) {
    const itemSpliceIndex = this._hash[item[this._key]] + 1
    const newStack = this.history.splice(0, itemSpliceIndex)
    this.history = newStack
    this._setHashToHistory()
  }

  _preserveDomainStackHistory () {
    const historyCopy = this.history.slice()
    this._historyCache[this._currentDomain] = historyCopy
  }

  _replayDomainHistory () {
    const cachedHistory = this._historyCache[this._currentDomain]
    if (cachedHistory) {
      this.history = cachedHistory
      this._setHashToHistory()
    }
  }

  _scrapeStackToAnchor (item) {
    // this._appendToStack(item)
    // const newStack = this.history.filter(i => this._isAnchor(i))
    // this.history = newStack
    this.resetToRoot()
    this._appendToStack(item)
    this._setHashToHistory()
  }

  _setHashToHistory () {
    this._hash = this.history.reduce((acc, i, index) => {
      acc[i[this._key]] = index
      return acc
    }, {})
  }

  _appendToStack (item) {
    this._hash[item[this._key]] = this.history.length
    const newStack = this.history.slice()
    newStack.push(item)
    this.history = newStack
  }

  _extractWithName (route) {
    if (Array.isArray(this._name)) {
      let retName = route
      this._name.forEach(n => {
        if (retName[n]) retName = retName[n]
      })
      return typeof retName === 'string' ? retName : route[this._key]
    } else if (typeof this._name === 'string') {
      return typeof route[this._name] === 'string' ? route[this._name] : route[this._key]
    }
  }

  _shouldIgnore (route) {
    return route.meta && route.meta[this._ignoreFlag]
  }
}

export let Breadcrumbs = new BreadCrumbsPlugin()
